import { state } from './state.js';
import { check_in_bounds } from './markers.js';
import { placeFetchDescription } from './api.js';
import { scrollStartStopHandler, escapeHtml as escapeAttr } from './helpers.js';
import { announceMapStatus } from './ui.js';
import _ from 'lodash';

function getSavedInterests() {
	try {
		const saved = localStorage.getItem('bcc_interests');
		return saved ? JSON.parse(saved) : [];
	} catch(e) { return []; }
}

function isAlreadyInterest(id) {
	return getSavedInterests().some(i => String(i.id) === String(id));
}

export function updateSidebarHearts() {
	const savedIds = new Set(getSavedInterests().map(i => String(i.id)));
	document.querySelectorAll('.sidebar-add-interest').forEach(btn => {
		const inInterests = savedIds.has(String(btn.dataset.postId));
		btn.classList.toggle('is-interest', inInterests);
		btn.style.background = inInterests ? '#007e00' : 'transparent';
		btn.style.borderColor = '#007e00';
		btn.style.color = inInterests ? '#fff' : '#007e00';
		btn.title = inInterests ? 'Already in your interests' : 'Add to interests';
		btn.setAttribute('aria-pressed', inInterests ? 'true' : 'false');
		btn.setAttribute('aria-label', inInterests ? `Remove ${btn.dataset.title} from interests` : `Add ${btn.dataset.title} to interests`);
		const icon = btn.querySelector('i');
		if (icon) {
			icon.className = inInterests ? 'fas fa-heart' : 'far fa-heart';
		}
	});
}

/**
 * Update Sidebar Content
 */
export function updateSideBar(force = false) {
	if ((state.isAnimating || state.isModalOpen) && !force) {
		return;
	}
	const sideBar = document.getElementById('sidebar-wrapper');
	const sideBarScrollElement = document.getElementById('map-sidebar');

	if (!sideBar.classList.contains('off-screen') || force === true) {
		const sideBarCardWrapper = document.getElementById("sidebar-card-wrapper");

		// Save scroll position
		const currentScroll = sideBarScrollElement ? sideBarScrollElement.scrollTop : 0;

		const clusterMarkers = state.markerCluster ?
			(typeof state.markerCluster.getMarkers === 'function' ? state.markerCluster.getMarkers() : (state.markerCluster.markers || [])) :
			[];

		const clusterMarkerSet = new Set(clusterMarkers);

		if (force === true) {
			if (state.sidebarObserver) {
				state.sidebarObserver.disconnect();
			}
			state.sidebarMarkers.markers = [];
			state.sidebarMarkerSet.clear();
			state.sidebarElementMap.clear();
			state.visibleSidebarItems.clear();
			sideBarCardWrapper.textContent = '';
		}

		if (!state.markers || state.markers.length === 0) {
			return;
		}

		const newCardsToAdd = [];
		const cardsToRemove = [];
		const bounds = state.map.getBounds();

		if (!bounds) return;

		// 1. Process Online-Only (Virtual) Businesses first
		const checkedValues = Array
			.from(document.querySelectorAll('#filters input[type="checkbox"]'))
			.filter((checkbox) => checkbox.checked)
			.map((checkbox) => parseInt(checkbox.value));

		const onlineOnlyFilter = document.getElementById('online-only-filter');
		const isOnlineFilterActive = onlineOnlyFilter ? onlineOnlyFilter.checked : false;

		// If filtering, add matching online businesses
		state.onlineOnlyBusinesses.businesses.forEach(business => {
			const matchesCat = checkedValues.length > 0 && business.location_category && business.location_category.some(catId => checkedValues.includes(catId));
			const isVisible = matchesCat || isOnlineFilterActive;
			const markerId = `ob_${business.id}`;

			if (isVisible) {
				if (!state.sidebarMarkerSet.has(markerId)) {
					state.sidebarMarkerSet.add(markerId);
					newCardsToAdd.push({
						isOnline: true,
						id: business.id,
						title: business.title,
						data: business
					});
				}
			} else {
				// Remove if not matching filter or if no filters active
				if (state.sidebarMarkerSet.has(markerId)) {
					const card = state.sidebarElementMap.get(markerId);
					if (card) {
						state.visibleSidebarItems.delete(markerId);
						cardsToRemove.push(card);
						state.sidebarElementMap.delete(markerId);
					}
					state.sidebarMarkerSet.delete(markerId);
				}
			}
		});

		// 2. Process physical markers — excluded when online-only filter is active
		if (isOnlineFilterActive) {
			// Remove any physical markers that were already in the sidebar
			for (let i = 0; i < state.markers.length; i++) {
				const markerId = state.markers[i].properties.post_id;
				if (state.sidebarMarkerSet.has(markerId)) {
					const card = state.sidebarElementMap.get(markerId);
					if (card) {
						state.visibleSidebarItems.delete(markerId);
						if (state.sidebarObserver) state.sidebarObserver.unobserve(card);
						cardsToRemove.push(card);
						state.sidebarElementMap.delete(markerId);
					}
					state.sidebarMarkerSet.delete(markerId);
					state.sidebarMarkers.markers = state.sidebarMarkers.markers.filter(el => el.properties.post_id !== markerId);
				}
			}
		}
		for (let i = 0; i < state.markers.length; i++) {
			if (isOnlineFilterActive) break;
			const marker = state.markers[i];
			// Only markers managed by the clusterer should be in the sidebar
			if (!marker.map && !clusterMarkerSet.has(marker)) continue;

			const inBounds = bounds.contains(marker.position);
			const markerId = marker.properties.post_id;

			if (inBounds) {
				if (!state.sidebarMarkerSet.has(markerId)) {
					state.sidebarMarkerSet.add(markerId);
					state.sidebarMarkers.markers.push(marker);
					newCardsToAdd.push({
						isOnline: false,
						id: markerId,
						title: marker.title,
						data: marker
					});
				}
			} else {
				if (force !== true) {
					if (state.sidebarMarkerSet.has(markerId)) {
						const card = state.sidebarElementMap.get(markerId);
						if (card) {
							state.visibleSidebarItems.delete(markerId);
							if (state.sidebarObserver) {
								state.sidebarObserver.unobserve(card);
							}
							cardsToRemove.push(card);
							state.sidebarElementMap.delete(markerId);
						}
						state.sidebarMarkerSet.delete(markerId);
						state.sidebarMarkers.markers = state.sidebarMarkers.markers.filter(el => el.properties.post_id !== markerId);
					}
				}
			}
		}

		// If focus is inside a card about to be removed (e.g. map panned while a
		// keyboard user was on a card button), move focus to the results heading
		// instead of letting it silently fall to <body>.
		const focusedCardToRemove = cardsToRemove.find(card => card.contains(document.activeElement));
		if (focusedCardToRemove) {
			const sidebarHeading = document.getElementById('map-sidebar-title');
			if (sidebarHeading) sidebarHeading.focus();
		}

		cardsToRemove.forEach(card => card.remove());

		if (newCardsToAdd.length > 0) {
			const fragment = document.createDocumentFragment();

			newCardsToAdd.forEach(item => {
				const title = item.title;
				const id = item.isOnline ? `ob_${item.id}` : item.id;
				const postId = item.id; // raw id for interests (no ob_ prefix)
				let buttonHTML = '';
				let tagHTML = '';

				if (item.isOnline) {
					tagHTML = `
						<div class="online-marker-pill" style="margin-bottom: 12px; display: inline-flex; align-items: center; background: #e8f5e9; border: 1px solid #007e00; border-radius: 20px; padding: 2px 12px; color: #007e00; font-size: 0.75rem; font-weight: bold;">
							<i class="fas fa-mouse-pointer" style="margin-right: 6px; font-size: 0.7rem;"></i> ONLINE ONLY
						</div>`;
					buttonHTML = `<button type="button" class="button online-profile-view" data-id="${item.id}" aria-label="View profile for ${escapeAttr(title)}">VIEW PROFILE</button>`;
				} else {
					const marker = item.data;
					let lat = marker.position.lat;
					let lng = marker.position.lng;
					if (typeof lat === 'function') lat = lat();
					if (typeof lng === 'function') lng = lng();
					buttonHTML = `<button type="button" class="info-window-trigger-button button" data-lat="${lat}" data-long="${lng}" data-place-id="${marker.properties.place_id}" data-post-id="${id}" aria-label="View profile for ${escapeAttr(title)}">VIEW PROFILE</button>`;
				}

				const alreadyAdded = isAlreadyInterest(postId);
				const heartStyle = alreadyAdded
					? 'background:#007e00;border:2px solid #007e00;color:#fff;'
					: 'background:transparent;border:2px solid #007e00;color:#007e00;';
				const heartIcon = alreadyAdded ? 'fas fa-heart' : 'far fa-heart';
				const heartHTML = `<button type="button" class="sidebar-add-interest${alreadyAdded ? ' is-interest' : ''}" data-post-id="${postId}" data-title="${escapeAttr(title)}" title="${alreadyAdded ? 'Already in your interests' : 'Add to interests'}" aria-pressed="${alreadyAdded ? 'true' : 'false'}" aria-label="${alreadyAdded ? `Remove ${escapeAttr(title)} from interests` : `Add ${escapeAttr(title)} to interests`}" style="display:flex;align-items:center;justify-content:center;width:38px;height:100%;min-height:38px;border-radius:4px;${heartStyle}"><i class="${heartIcon}" aria-hidden="true"></i></button>`;

				const html = `
					<div class="grid-container full padb2 padt2 profile-snippet" data-loaded="false" data-marker-id="${escapeAttr(id)}" role="listitem">
						<div class="grid-x"><div class="cell auto">
						<h2 class="profile-snippet-title">${escapeAttr(title)}</h2></div></div>
						<div class="grid-x"><div class="cell auto">
							${tagHTML}
                            <div class="profile-snippet-categories"></div>
                            <div class="profile-snippet-description blur">Loading description...</div>
                            <div class="profile-snippet-keywords padt1"></div>
                        </div></div>
						<div class="grid-x align-middle">
							<div class="cell shrink" style="padding-right:6px;">${buttonHTML}</div>
							<div class="cell shrink">${heartHTML}</div>
						</div></div>`;

				const tempDiv = document.createElement('div');
				tempDiv.innerHTML = html;
				const cardNode = tempDiv.firstElementChild;
				state.sidebarElementMap.set(id, cardNode);
				fragment.appendChild(cardNode);
			});

			sideBarCardWrapper.appendChild(fragment);

			// Re-sort alphabetically (mixed together)
			const sortedCards = [...sideBarCardWrapper.children]
				.sort((a, b) => {
					const idA = a.dataset.markerId;
					const idB = b.dataset.markerId;

					// Prioritize last viewed business at the top (UX win)
					if (state.lastViewedId) {
						if (idA == state.lastViewedId) return -1;
						if (idB == state.lastViewedId) return 1;
					}

					const titleA = a.querySelector('.profile-snippet-title').innerText;
					const titleB = b.querySelector('.profile-snippet-title').innerText;
					return titleA.localeCompare(titleB);
				});

			sortedCards.forEach(node => sideBarCardWrapper.appendChild(node));
		}

		// Restore scroll position after DOM updates
		if (sideBarScrollElement && currentScroll > 0) {
			requestAnimationFrame(() => {
				sideBarScrollElement.scrollTop = currentScroll;
			});
		}

		if (state.sidebarObserver) {
			newCardsToAdd.forEach(item => {
				const id = item.isOnline ? `ob_${item.id}` : item.id;
				const card = state.sidebarElementMap.get(id);
				if (card) state.sidebarObserver.observe(card);
			});
		}

		if (sideBarCardWrapper) {
			const count = sideBarCardWrapper.children.length;
			announceMapStatus(`${count} business ${count === 1 ? 'result' : 'results'} available in the list.`);
		}
	}
}

/**
 * Load Card Data (Description and Keywords)
 */
export function loadCardData(card, marker) {
	if (!card || card.dataset.loaded === "true") {
		return;
	}

	// Mark as loaded immediately
	card.dataset.loaded = "true";

	const id = card.dataset.markerId;
	const isOnline = id.toString().startsWith('ob_');
	const descContainer = card.querySelector('.profile-snippet-description');
	const keywordsContainer = card.querySelector('.profile-snippet-keywords');
	const catContainer = card.querySelector('.profile-snippet-categories');

	let data = null;
	if (isOnline) {
		const realId = parseInt(id.replace('ob_', ''));
		data = state.onlineOnlyBusinesses.businesses.find(b => b.id === realId);
	} else {
		data = marker;
	}

	if (!data) return;

	// Categories & Keywords
	if (isOnline) {
		if (data.description) {
			descContainer.innerHTML = data.description.substring(0, 150) + '...';
			descContainer.classList.remove('blur');
		}

		// Render categories for Online Businesses
		if (data.location_category && Array.isArray(data.location_category)) {
			if (catContainer) catContainer.innerHTML = '';
			data.location_category.forEach(catId => {
				// We need to find the category name from state.categoryColors or similar
				// But online business data usually has the name or we can resolve it
				const catName = data.category_names?.[catId] || 'Business';
				const span = document.createElement('span');
				span.className = 'cat-active';
				span.style.background = '#007e00'; // Default green for virtual
				span.style.color = '#fff';
				span.style.fontWeight = 'bold';
				span.textContent = catName;
				catContainer.appendChild(span);
			});
		}
	} else {
		// Physical markers logic (already exists)
		const categoryTitleStr = data.properties.category_title;
		const markerColor = data.properties.color ? `#${data.properties.color.replace('#', '')}` : '#999';

		if (categoryTitleStr) {
			const categories = categoryTitleStr
				.replace(/&amp;/g, '&')
				.split(',')
				.map(cat => cat.trim().replace(/,+$/, ''))
				.filter(cat => cat !== '');

			categories.forEach(cat => {
				const span = document.createElement('span');
				span.className = 'cat-active';
				const catColor = state.categoryColors[cat] || markerColor;
				span.style.background = catColor;
				span.style.color = '#fff';
				span.style.fontWeight = 'bold';
				span.textContent = cat;
				catContainer.appendChild(span);
			});
		}

		const keywords = data.properties.keywords;
		if (keywords) {
			const kwArray = typeof keywords === 'string' ? keywords.split(',').map(kw => kw.trim()).filter(kw => kw !== '') : [];
			kwArray.forEach(kw => {
				const span = document.createElement('span');
				span.className = 'cat-active';
				span.textContent = kw;
				keywordsContainer.appendChild(span);
			});
		}

		placeFetchDescription(data.properties.post_id).then(response => {
			if (response && response[0]?.acf?.location_description) {
				descContainer.innerHTML = String(response[0].acf.location_description).substring(0, 150) + '...';
			} else {
				descContainer.innerHTML = "No description available.";
			}
		}).catch(() => {
			descContainer.innerHTML = "No description available.";
		}).finally(() => {
			if (descContainer) descContainer.classList.remove('blur');
		});
	}
}

/**
 * Initialize Sidebar Observer
 */
export function initObserver() {
	if (state.sidebarObserverInitialized) {
		return;
	}
	state.sidebarObserverInitialized = true;

	const loadVisibleData = _.throttle(function() {
		if (state.isScrolling || state.visibleSidebarItems.size === 0) {
			return;
		}

		let loadCount = 0;
		const maxSimultaneousLoads = 5;

		for (const cardId of state.visibleSidebarItems) {
			if (loadCount >= maxSimultaneousLoads) break;

			const card = document.querySelector(`#sidebar-card-wrapper [data-marker-id="${cardId}"]`);
			if (!card || card.dataset.loaded === "true") continue;

			const isOnline = cardId.toString().startsWith('ob_');
			if (isOnline) {
				loadCardData(card, null);
				loadCount++;
			} else {
				const marker = state.markerMap.get(parseInt(cardId)) || state.markerMap.get(cardId);
				if (marker) {
					loadCardData(card, marker);
					loadCount++;
				}
			}
		}
	}, 500);

	function intersectionCallback(entries) {
		entries.forEach(function (entry) {
			if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
				entry.target.classList.remove('off-screen-card');
				entry.target.classList.add('on-screen-card');
				state.visibleSidebarItems.add(entry.target.dataset.markerId);

				if (!state.isScrolling && entry.target.dataset.loaded !== "true") {
					const cardId = entry.target.dataset.markerId;
					const isOnline = cardId.toString().startsWith('ob_');

					if (isOnline) {
						loadCardData(entry.target, null);
					} else {
						const marker = state.markerMap.get(parseInt(cardId)) || state.markerMap.get(cardId);
						if (marker) {
							loadCardData(entry.target, marker);
						}
					}
				} else if (state.isScrolling) {
					state.pendingImageLoads.add(entry.target.dataset.markerId);
				}
			} else {
				state.visibleSidebarItems.delete(entry.target.dataset.markerId);
				state.pendingImageLoads.delete(entry.target.dataset.markerId);
				entry.target.classList.remove('on-screen-card');
				entry.target.classList.add('off-screen-card');
			}
		});

		if (!state.isScrolling) {
			loadVisibleData();
		}
	}

	const sideBarParent = document.getElementById('map-sidebar');
	if (sideBarParent) {
		scrollStartStopHandler(
			sideBarParent,
			() => {},
			() => {
				loadVisibleData();
			}
		);
	}

	const observerOptions = {
		rootMargin: '50px',
		threshold: 0.5
	};

	state.sidebarObserver = new IntersectionObserver(intersectionCallback, observerOptions);
}
